import React, { useMemo, useState } from "react";
import { calcEMI, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, DarkCTA, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

// Typical rates in October 2026 (repo 5.50%): home loans ~7.25-8.5%, car
// ~8.15%+, personal ~10%+ for strong profiles. Picking a type sets these.
const LOAN_PRESETS = {
  "Home Loan": { rate: 8.0, years: 20 },
  "Personal Loan": { rate: 11.5, years: 5 },
  "Car Loan": { rate: 9.0, years: 5 },
  "Business Loan": { rate: 14.0, years: 5 },
};
const loanTypes = Object.keys(LOAN_PRESETS);

export const EMITab = () => {
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(LOAN_PRESETS["Home Loan"].rate);
  const [years, setYears] = useState(LOAN_PRESETS["Home Loan"].years);
  const [loanType, setLoanType] = useState("Home Loan");

  const chooseType = (type) => {
    setLoanType(type);
    setRate(LOAN_PRESETS[type].rate);
    setYears(LOAN_PRESETS[type].years);
  };

  const result = useMemo(() => calcEMI(amount, rate, years), [amount, rate, years]);
  const animKey = `emi-${Math.round(result.emi)}-${amount}-${rate}-${years}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Loan Amount"
          valueText={`Rs. ${formatINR(amount)}`}
          valueTestId="calc-loan-amount-value"
          finePrint={<><span>Rs.50,000</span><span>Rs.1 Crore</span></>}
        >
          <Slider value={amount} min={50000} max={10000000} step={10000} onChange={setAmount} ariaLabel="Loan amount" />
        </InputRow>

        <InputRow
          label="Annual Interest Rate"
          valueText={`${rate.toFixed(1)}% per annum`}
          valueTestId="calc-rate-value"
          finePrint={<><span>6%</span><span>24% per annum</span></>}
        >
          <Slider value={rate} min={6} max={24} step={0.1} onChange={setRate} ariaLabel="Annual interest rate" />
        </InputRow>

        <InputRow
          label="Loan Tenure"
          valueText={`${years} ${years === 1 ? "Year" : "Years"} (${years * 12} Months)`}
          valueTestId="calc-tenure-value"
          finePrint={<><span>1 year</span><span>30 years</span></>}
        >
          <Slider value={years} min={1} max={30} step={1} onChange={setYears} ariaLabel="Loan tenure" />
        </InputRow>

        <div>
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>Loan Type</div>
          <Pills items={loanTypes} value={loanType} onChange={chooseType} prefix="calc-loan-type" />
          <p className="mt-3" style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
            Each loan type sets a typical October 2026 rate and tenure. Your actual rate depends on the lender and
            your credit score, so adjust the sliders to match your offer.
          </p>
        </div>
      </div>

      <RightPanel testId="calculator-results">
        <HeroValue label="Monthly EMI" value={`Rs. ${formatINR(result.emi)}`} animKey={animKey} testId="calc-emi-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Principal Amount" value={`Rs. ${formatINR(amount)}`} testId="calc-row-principal" />
          <ResultRow label="Total Interest" value={`Rs. ${formatINR(result.totalInterest)}`} valueColor="#F4A700" testId="calc-row-interest" />
          <ResultRow label="Total Payment" value={`Rs. ${formatINR(result.totalPayment)}`} testId="calc-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.principalPct}
          rightPct={result.interestPct}
          leftLabel="Principal"
          rightLabel="Interest"
          leftColor="#1D9E75"
          rightColor="#F4A700"
          testId="calc-split-bar"
        />
        <DarkCTA href="mailto:loans@metamonk.co" testId="calc-apply-cta">
          Apply for a Loan via Meta Monk
        </DarkCTA>
      </RightPanel>
    </div>
  );
};
